import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useRegion } from "@/config/RegionContext";
import { useNbiForm } from "../hooks/useNbiForm";
import { useApplicationRecovery } from "../hooks/useApplicationRecovery";

interface NbiFormContextValue {
    form: ReturnType<typeof useNbiForm>;
    isRecovering: boolean;
    isCreating: boolean;
    hasInitialized: boolean;
    initError: string | null;
    refreshApplication: (appId: number) => Promise<void>;
}

const NbiFormContext = createContext<NbiFormContextValue | null>(null);

export const useNbiFormContext = (): NbiFormContextValue => {
    const context = useContext(NbiFormContext);
    if (!context) {
        throw new Error("useNbiFormContext must be used within a NbiFormProvider");
    }
    return context;
};

export const useNbiFormContextSafe = (): NbiFormContextValue | null => {
    return useContext(NbiFormContext);
};

interface NbiFormProviderProps {
    children: ReactNode;
}

export const NbiFormProvider: React.FC<NbiFormProviderProps> = ({ children }) => {
    const region = useRegion();
    const isUS = region.code === "US";
    const regionDefaults = useMemo(
        () =>
            isUS
                ? { citizenship: "American", country: "United States", state: "CA", currencyCode: "USD" }
                : { citizenship: "Australian", country: "Australia", state: "NSW", currencyCode: "AUD" },
        [isUS],
    );
    const form = useNbiForm(regionDefaults);
    const { isRecovering, isCreating, hasInitialized, error: initError, refreshApplication } = useApplicationRecovery({ form, regionDefaults });

    return (
        <NbiFormContext.Provider value={{ form, isRecovering, isCreating, hasInitialized, initError, refreshApplication }}>
            {children}
        </NbiFormContext.Provider>
    );
};
